import { Stack, Typography, TypographyProps } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { LinkButton } from '@/components/form/baseInputs/Link/LinkButton';
import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { CheckFilledIcon } from '@/components/icons/CheckFilledIcon';
import { useFormContext } from '@/components/react-hook-form';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsFormShape,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { useDeathOrderAwareClient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import {
  calculateResiduaryRecipients,
  getDeathSectionDistributionStatus,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import {
  DeathSectionDistributionStatus,
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { COLORS } from '@/styles/tokens/colors';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { getPulidKind, PulidKind } from '@/utils/pulid';

interface DistributionStatusConfigs {
  color: string;
  StatusIcon: React.ElementType | null;
  label: React.ReactNode;
  typographyVariant: TypographyProps['variant'];
}

function useTemplateDistributionStatus(): DeathSectionDistributionStatus {
  const { control } = useFormContext<DispositiveProvisionsFormShape>();
  const recipients = useWatch({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
  });

  return useMemo<DeathSectionDistributionStatus>(() => {
    if (!recipients?.length)
      return DeathSectionDistributionStatus.ASSETS_REMAIN;

    // Delegate to the same logic the death-event path uses so the two
    // codepaths stay in sync.
    const { isFullyDistributed, exceeds100Percent } =
      calculateResiduaryRecipients(recipients);

    if (exceeds100Percent) {
      return DeathSectionDistributionStatus.EXCEEDS_100_PERCENT;
    }
    if (isFullyDistributed) {
      return DeathSectionDistributionStatus.FULLY_DISTRIBUTED;
    }
    return DeathSectionDistributionStatus.NEEDS_RESIDUARY;
  }, [recipients]);
}

interface PrimaryClientDeathSectionDistributionStatusPropsBase {
  deathEvent?: PrimaryClientDeathEvent | undefined;
  recipients?: DispositiveProvisionsFormRecipient[] | undefined;
  templateKind?: DispositiveProvisionTemplateKind | undefined;
  onAddRecipient?: (
    prefilledValues?: Partial<DispositiveProvisionsFormRecipient>
  ) => void;
}

interface PropsWithDeathEvent
  extends PrimaryClientDeathSectionDistributionStatusPropsBase {
  deathEvent: PrimaryClientDeathEvent;
}

interface PropsWithDispositionTemplate
  extends PrimaryClientDeathSectionDistributionStatusPropsBase {
  templateKind: DispositiveProvisionTemplateKind;
}

type PrimaryClientDeathSectionDistributionStatusProps =
  | PropsWithDeathEvent
  | PropsWithDispositionTemplate;

export function PrimaryClientDeathSectionDistributionStatus({
  deathEvent,
  templateKind,
  onAddRecipient,
}: PrimaryClientDeathSectionDistributionStatusProps) {
  // Either deathEvent or dispositionTemplate must be provided
  if (!deathEvent && !templateKind) {
    throw new Error('Either deathEvent or template kind must be provided');
  }
  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();

  const canEditHouseholdData = useCanEditDispositiveProvisions();

  const { clientProfileOrEntityOrTestamentaryEntityId } =
    useDispositiveProvisionsContext();

  const dyingPrimaryClient = useDeathOrderAwareClient(
    deathEvent ? { deathEvent } : undefined
  );

  const firstDeathEventClient = useDeathOrderAwareClient({
    deathEvent: PrimaryClientDeathEvent.FIRST_DEATH,
  });

  const recipientsData = useWatch({
    control,
    name: [
      `${getDeathEventBasePath(PrimaryClientDeathEvent.FIRST_DEATH)}.recipients`,
      `${getDeathEventBasePath(PrimaryClientDeathEvent.SECOND_DEATH)}.recipients`,
    ],
    disabled: !deathEvent,
  }) as [
    DispositiveProvisionsFormRecipient[] | null | undefined,
    DispositiveProvisionsFormRecipient[] | null | undefined,
  ];

  const [firstDeathRecipients, secondDeathRecipients] = deathEvent
    ? recipientsData
    : [null, null];

  const templateStatus = useTemplateDistributionStatus();
  const status = useMemo(() => {
    if (templateKind) {
      return templateStatus;
    }

    if (!deathEvent) {
      return null;
    }

    return getDeathSectionDistributionStatus(
      deathEvent,
      firstDeathRecipients,
      secondDeathRecipients
    );
  }, [
    deathEvent,
    firstDeathRecipients,
    secondDeathRecipients,
    templateKind,
    templateStatus,
  ]);

  // Check if we're viewing an organization
  const isViewingOrganization = useMemo(() => {
    if (templateKind) {
      return false;
    }

    return (
      getPulidKind(clientProfileOrEntityOrTestamentaryEntityId) ===
      PulidKind.ClientOrganization
    );
  }, [clientProfileOrEntityOrTestamentaryEntityId, templateKind]);

  const handleAddResiduary = useCallback(() => {
    onAddRecipient?.({
      dispositionKind_ButtonGroup: 'other',
      dispositionKind_Select:
        DispositiveProvisionDispositionKind.AnythingLeftOver,
    });
  }, [onAddRecipient]);

  const statusConfigs = useMemo<DistributionStatusConfigs | null>(() => {
    if (status == null) {
      return null;
    }

    switch (status) {
      case DeathSectionDistributionStatus.ASSETS_REMAIN:
        return {
          color: COLORS.GRAY['500'],
          StatusIcon: null,
          label: 'Undistributed assets remain',
          typographyVariant: 'body-xs',
        };
      case DeathSectionDistributionStatus.FULLY_DISTRIBUTED:
        return {
          color: COLORS.TEAL['600'],
          StatusIcon: CheckFilledIcon,
          label: dyingPrimaryClient?.displayGivenName
            ? `Fully distributed after ${dyingPrimaryClient.displayGivenName}'s death`
            : 'Fully distributed',
          typographyVariant: 'title-xs',
        };
      case DeathSectionDistributionStatus.NEEDS_RESIDUARY:
        return {
          color: COLORS.FUNCTIONAL.WARNING['600'],
          StatusIcon: AlertTriangleFilledIcon,
          label: (
            <>
              Undistributed assets remain
              {!isViewingOrganization && canEditHouseholdData && (
                <>
                  {EMPTY_CONTENT_HYPHEN}
                  <LinkButton
                    color={COLORS.FUNCTIONAL.WARNING['600']}
                    display="add residuary"
                    onClick={handleAddResiduary}
                  />
                </>
              )}
            </>
          ),
          typographyVariant: 'title-xs',
        };
      case DeathSectionDistributionStatus.EXCEEDS_100_PERCENT:
        return {
          color: COLORS.FUNCTIONAL.WARNING['600'],
          StatusIcon: AlertTriangleFilledIcon,
          label: 'Distributions specified exceed 100%',
          typographyVariant: 'title-xs',
        };
      case DeathSectionDistributionStatus.NO_ASSETS_REMAIN:
        const deathLabel = firstDeathEventClient?.displayGivenName
          ? `${firstDeathEventClient.displayGivenName}'s death`
          : 'first death';
        return {
          color: COLORS.GRAY['500'],
          StatusIcon: null,
          label: `Selected entity fully distributes after ${deathLabel}, select a tile to manage dispositions`,
          typographyVariant: 'body-xs',
        };
      default:
        throw new UnreachableError({
          case: status,
          message: `[PrimaryClientDeathSectionDistributionStatus] Unknown distribution status: ${status}`,
        });
    }
  }, [
    canEditHouseholdData,
    dyingPrimaryClient.displayGivenName,
    firstDeathEventClient.displayGivenName,
    handleAddResiduary,
    isViewingOrganization,
    status,
  ]);

  if (status == null || statusConfigs == null) {
    return null;
  }

  const { color, StatusIcon, label, typographyVariant } = statusConfigs;

  return (
    <Stack direction="row" gap={1} alignItems="center">
      {StatusIcon && <StatusIcon color={color} size={16} />}
      <Typography variant={typographyVariant} color={color}>
        {label}
      </Typography>
    </Stack>
  );
}
